import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { AppButton } from './AppButton';
import { AppInput } from './AppInput';
import type { SelectorOption } from './SearchableEntitySelector';

type Props = {
  label: string;
  selected: SelectorOption[];
  loadOptions: (search: string) => Promise<SelectorOption[]> | SelectorOption[];
  onChange: (selected: SelectorOption[]) => void;
  /** Selected ids that can be seen but not removed. */
  lockedIds?: number[];
  error?: string;
  disabled?: boolean;
  placeholder?: string;
};

const SEARCH_DEBOUNCE_MS = 400;

export function SearchableMultiSelect({
  label,
  selected,
  loadOptions,
  onChange,
  lockedIds = [],
  error,
  disabled,
  placeholder = 'Search roles',
}: Props) {
  const palette = useAppColors();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SelectorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const requestId = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const runSearch = useCallback((value: string) => {
    const current = ++requestId.current;
    if (!mounted.current) return;
    setLookupError(null);

    const apply = (result: SelectorOption[]) => {
      if (!mounted.current || current !== requestId.current) return;
      setOptions(result);
      setLoading(false);
    };
    const fail = () => {
      if (!mounted.current || current !== requestId.current) return;
      setOptions([]);
      setLookupError('Unable to load options.');
      setLoading(false);
    };

    let pending: Promise<SelectorOption[]> | SelectorOption[];
    try {
      pending = loadOptionsRef.current(value.trim());
    } catch {
      fail();
      return;
    }

    if (pending && typeof (pending as Promise<SelectorOption[]>).then === 'function') {
      setLoading(true);
      (pending as Promise<SelectorOption[]>).then(apply, fail);
      return;
    }

    apply((pending as SelectorOption[]) ?? []);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestId.current += 1;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  function openPicker() {
    if (disabled) return;
    setOpen(true);
    setSearch('');
    setOptions([]);
    setLookupError(null);
    runSearch('');
  }

  function closePicker() {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setOpen(false);
    setSearch('');
    setOptions([]);
    setLookupError(null);
  }

  function onSearchChange(value: string) {
    setSearch(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      runSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  function toggle(option: SelectorOption) {
    const exists = selected.some(item => item.id === option.id);
    if (exists) {
      if (lockedIds.includes(option.id)) return;
      onChange(selected.filter(item => item.id !== option.id));
      return;
    }
    onChange([...selected, option]);
  }

  function remove(option: SelectorOption) {
    if (lockedIds.includes(option.id)) return;
    onChange(selected.filter(item => item.id !== option.id));
  }

  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: palette.textSecondary }]}>{label}</Text>
      {selected.length > 0 ? (
        <View style={styles.chips}>
          {selected.map(option => {
            const locked = lockedIds.includes(option.id);
            return (
              <View
                key={option.id}
                style={[
                  styles.chip,
                  { borderColor: palette.primary, backgroundColor: palette.surface },
                ]}
              >
                <Text
                  style={[styles.chipLabel, { color: palette.textPrimary }]}
                  numberOfLines={1}
                >
                  {option.label}
                </Text>
                {locked ? null : (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Remove ${option.label}`}
                    hitSlop={8}
                    onPress={() => remove(option)}
                    style={styles.removeButton}
                  >
                    <Text style={[styles.removeLabel, { color: palette.textSecondary }]}>
                      ×
                    </Text>
                  </Pressable>
                )}
              </View>
            );
          })}
        </View>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ expanded: open, disabled: Boolean(disabled) }}
        disabled={disabled}
        onPress={open ? closePicker : openPicker}
        style={[
          styles.dropdownButton,
          { backgroundColor: palette.surface, borderColor: palette.border },
          disabled && styles.disabled,
        ]}
      >
        <Text style={[styles.dropdownLabel, { color: palette.textMuted }]} numberOfLines={1}>
          {selected.length > 0 ? 'Add another role' : 'Select roles'}
        </Text>
        <Text style={{ color: palette.textMuted }}>{open ? '▴' : '▾'}</Text>
      </Pressable>
      {open && !disabled ? (
        <View
          style={[
            styles.panel,
            { borderColor: palette.border, backgroundColor: palette.surface },
          ]}
        >
          <AppInput
            label="Search"
            value={search}
            onChangeText={onSearchChange}
            placeholder={placeholder}
            autoCapitalize="none"
          />
          {loading ? (
            <ActivityIndicator color={palette.primary} />
          ) : lookupError ? (
            <View>
              <Text style={[styles.help, { color: palette.error }]}>{lookupError}</Text>
              <AppButton title="Retry" variant="secondary" onPress={() => runSearch(search)} />
            </View>
          ) : options.length === 0 ? (
            <Text style={[styles.help, { color: palette.textMuted }]}>No matches found.</Text>
          ) : (
            options.map(option => {
              const isSelected = selected.some(item => item.id === option.id);
              return (
                <Pressable
                  key={option.id}
                  accessibilityRole="button"
                  accessibilityLabel={option.label}
                  accessibilityState={{ selected: isSelected }}
                  style={[styles.option, { borderBottomColor: palette.border }]}
                  onPress={() => toggle(option)}
                >
                  <View style={styles.optionText}>
                    <Text style={[styles.optionTitle, { color: palette.textPrimary }]}>
                      {option.label}
                    </Text>
                    {option.subtitle ? (
                      <Text style={[styles.help, { color: palette.textSecondary }]}>
                        {option.subtitle}
                      </Text>
                    ) : null}
                  </View>
                  {isSelected ? (
                    <Text style={{ color: palette.primary }}>✓</Text>
                  ) : null}
                </Pressable>
              );
            })
          )}
          <AppButton title="Done" variant="secondary" onPress={closePicker} />
        </View>
      ) : null}
      {error ? (
        <Text accessibilityRole="alert" style={[styles.help, { color: palette.error }]}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  label: {
    ...typography.caption,
    fontWeight: '600',
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '100%',
    minHeight: 36,
    borderWidth: 1,
    borderRadius: 999,
    paddingLeft: spacing.md,
    paddingRight: spacing.xs,
    gap: spacing.xs,
  },
  chipLabel: {
    ...typography.caption,
    flexShrink: 1,
  },
  removeButton: {
    minWidth: 32,
    minHeight: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeLabel: {
    ...typography.body,
    fontSize: 18,
    lineHeight: 22,
  },
  dropdownButton: {
    minHeight: 48,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  dropdownLabel: {
    ...typography.body,
    flex: 1,
  },
  disabled: { opacity: 0.6 },
  panel: {
    borderWidth: 1,
    borderRadius: 10,
    padding: spacing.md,
    gap: spacing.sm,
  },
  option: {
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  optionText: { flex: 1, minWidth: 0 },
  optionTitle: { ...typography.body },
  help: { ...typography.caption },
});
